/**
 * Animation pattern functions for the onboarding dot grid.
 *
 * Each pattern takes a time value (seconds) and grid dimensions (rows, cols),
 * and returns a Float32Array of brightness values (0..1) for each dot.
 * Brightness 0 = base color, 1 = bright color.
 *
 * Ported from the reference implementation at dot-grid-animation.netlify.app,
 * adapted to support non-square grids.
 */

export type DotGridAnimationMode = "agents" | "orchestration" | "governance";

export type PatternFn = (t: number, rows: number, cols: number) => Float32Array;

function smooth(x: number): number {
    return x * x * (3 - 2 * x);
}

function clamp01(x: number): number {
    return Math.max(0, Math.min(1, x));
}

function softer(x: number): number {
    return smooth(clamp01(x));
}

// Each animation plays for ACTIVE_DURATION seconds, then idles for IDLE_DURATION.
const ACTIVE_DURATION = 6;
const IDLE_DURATION = 12;
const TOTAL_CYCLE = ACTIVE_DURATION + IDLE_DURATION;
const DIM = 0.08;

/** Returns the animation phase (0..1) within the active portion, or -1 if idle. */
function cyclePhase(t: number): number {
    const inCycle = ((t % TOTAL_CYCLE) + TOTAL_CYCLE) % TOTAL_CYCLE;
    if (inCycle >= ACTIVE_DURATION) return -1;
    return inCycle / ACTIVE_DURATION;
}

/**
 * Agents: diagonal 2x2 blocks build from bottom-left to top-right, hold,
 * then a diagonal wave sweeps across fading each block as it passes.
 *
 * Faithful port of the reference (dot-grid-animation.netlify.app) with an
 * idle gap between cycles. Runs in a virtual 10x10 grid and maps canvas
 * dots into that space to keep blocks tightly packed.
 */
function patternAgents(t: number, rows: number, cols: number): Float32Array {
    const phase = cyclePhase(t);

    // During idle, return uniform dim
    if (phase < 0) {
        const out = new Float32Array(rows * cols);
        out.fill(DIM);
        return out;
    }

    const g = 10;
    const blkSize = 2;
    const nBlocks = 5;

    type Block = { sr: number; sc: number };
    const blocks: Block[] = [];
    for (let i = 0; i < nBlocks; i++) {
        const frac = i / (nBlocks - 1 || 1);
        const sr = Math.round((g - blkSize) * (1 - frac));
        const sc = Math.round((g - blkSize) * frac);
        blocks.push({ sr, sc });
    }

    // Phase timings (matching reference exactly)
    const buildStart = 0.06;
    const buildEnd = 0.42;
    const fadeStart = 0.52;
    const fadeEnd = 0.9;
    const diagMax = (g - 1) * 2;
    const fadeBandWidth = 1.8;
    const firstBlock = blocks[0];

    const vGrid = new Float32Array(g * g);
    for (let vr = 0; vr < g; vr++) {
        for (let vc = 0; vc < g; vc++) {
            let v = DIM;
            const cellDiag = (g - 1 - vr) + vc;

            for (let b = 0; b < nBlocks; b++) {
                const { sr, sc } = blocks[b];
                const inBlock = vr >= sr && vr < sr + blkSize && vc >= sc && vc < sc + blkSize;
                if (!inBlock) continue;

                if (phase < buildStart) {
                    // First block visible before build sequence starts
                    if (b === 0) v = DIM + 0.9 * smooth(1);
                    break;
                }
                if (phase < buildEnd) {
                    // Blocks appear sequentially
                    const buildT = (phase - buildStart) / (buildEnd - buildStart);
                    const blockFront = buildT * (nBlocks + 0.3);
                    const edge = 0.35;
                    const inness = (blockFront - b) / edge;
                    v = Math.max(v, DIM + 0.9 * smooth(clamp01(inness)));
                    break;
                }
                if (phase < fadeStart) {
                    // All blocks fully lit
                    v = Math.max(v, 0.98);
                    break;
                }
                // Diagonal wave sweeps, fading each block as it passes
                const fadeT = (phase - fadeStart) / (fadeEnd - fadeStart);
                const waveDiag = fadeT * (diagMax + fadeBandWidth * 2);
                const distToWave = Math.abs(cellDiag - waveDiag);
                const inBand = Math.max(0, 1 - distToWave / fadeBandWidth);
                v = Math.max(v, DIM + 0.9 * smooth(inBand));
                break;
            }

            // Subtle flicker on dots surrounding the first block before build starts
            if (phase < buildStart && firstBlock) {
                const distToFirst = Math.min(
                    Math.abs(vr - firstBlock.sr) + Math.abs(vc - firstBlock.sc),
                    Math.abs(vr - firstBlock.sr - (blkSize - 1)) + Math.abs(vc - firstBlock.sc),
                    Math.abs(vr - firstBlock.sr) + Math.abs(vc - firstBlock.sc - (blkSize - 1)),
                    Math.abs(vr - firstBlock.sr - (blkSize - 1)) + Math.abs(vc - firstBlock.sc - (blkSize - 1)),
                );
                const isSurrounding = distToFirst >= 1 && distToFirst <= 2.5;
                if (isSurrounding) {
                    const flicker = 0.5 + 0.5 * Math.sin(t * 4.2 + vr * 0.8 + vc * 0.6);
                    v = Math.max(v, DIM + 0.18 * smooth(clamp01(flicker)));
                }
            }

            vGrid[vr * g + vc] = Math.min(1, v);
        }
    }

    // Map canvas dots to virtual grid
    const out = new Float32Array(rows * cols);
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const vr = Math.min(g - 1, Math.floor((r / rows) * g));
            const vc = Math.min(g - 1, Math.floor((c / cols) * g));
            out[r * cols + c] = vGrid[vr * g + vc];
        }
    }
    return out;
}

/**
 * Orchestration: cascading rows — wave travels top → bottom with per-column stagger.
 */
function patternOrchestration(t: number, rows: number, cols: number): Float32Array {
    const phase = cyclePhase(t);
    const out = new Float32Array(rows * cols);

    if (phase < 0) {
        out.fill(DIM);
        return out;
    }

    const bandWidth = 2.2;
    const columnStagger = 0.35;
    const maxStagger = columnStagger * (cols - 1);
    const totalTravel = (rows - 1) + maxStagger + bandWidth * 2.5;
    const easedPhase = 1 - Math.pow(1 - phase, 1.3);
    const waveRow = easedPhase * totalTravel - bandWidth;

    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const effectiveWave = waveRow - c * columnStagger;
            const distToWave = Math.abs(r - effectiveWave);
            const inBand = Math.max(0, 1 - distToWave / bandWidth);
            const wave = softer(inBand);
            out[r * cols + c] = DIM + (0.92 - DIM) * wave;
        }
    }
    return out;
}

/**
 * Governance: traveling scan wave reveals a structured checkerboard pattern.
 */
function patternGovernance(t: number, rows: number, cols: number): Float32Array {
    const phase = cyclePhase(t);
    const out = new Float32Array(rows * cols);

    if (phase < 0) {
        out.fill(DIM);
        return out;
    }

    const bandWidth = 3.0;
    const totalTravel = rows + bandWidth * 2.5;
    const easedPhase = 1 - Math.pow(1 - phase, 1.3);
    const scanRow = easedPhase * totalTravel - bandWidth;

    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const distToScan = Math.abs(r - scanRow);
            const inBand = Math.max(0, 1 - distToScan / bandWidth);
            const wave = softer(inBand);
            const isCheck = (r + c) % 2 === 0;
            if (isCheck) {
                out[r * cols + c] = DIM + (0.92 - DIM) * wave;
            } else {
                out[r * cols + c] = DIM + (0.35 - DIM) * wave;
            }
        }
    }
    return out;
}

export const PATTERNS: Record<DotGridAnimationMode, PatternFn> = {
    agents: patternAgents,
    orchestration: patternOrchestration,
    governance: patternGovernance,
};
